import { Component } from '@theme/component';

/**
 * Where the sticky bar should stop following the user. Keyed by the
 * `sticky_atc_hide_at` section setting.
 */
const HIDE_AT = {
  related: [
    '.ui-test-product-list',
    '[id^="shopify-section-"][id*="__product_list"]',
    '.shopify-section-group-footer-group',
  ],
  footer: ['.shopify-section-group-footer-group'],
  never: [],
};

/** sessionStorage key for the ?atc_mode= testing override. */
const MODE_STORAGE_KEY = 'vb-sticky-atc-mode';

const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Keyboard-activated buttons dispatch click with detail 0; pointers report >= 1. */
const viaKeyboard = (event) => Boolean(event) && event.type === 'click' && event.detail === 0;

/**
 * Sticky add-to-cart bar for product pages.
 *
 * Owns no cart logic of its own: every add press is proxied to the section's
 * real add-to-cart button, so size validation ("Please select a size"), the
 * cart drawer, analytics and the "Added" animation behave exactly as inline.
 *
 * Two behaviours when no size is selected (data-size-mode):
 *   scroll — scroll the size picker into view, then press the real button so
 *            the theme's own prompt appears in front of the user.
 *   inline — open a size list (sheet on mobile, overlay on desktop) built from
 *            the real picker's radios; choosing one selects it in the picker.
 *
 * @typedef {object} StickyAddToCartRefs
 * @property {HTMLElement} bar - The black block (main + aside).
 * @property {HTMLButtonElement} main - Primary button (label / "Added").
 * @property {HTMLElement} label - Primary label, mirrored from the real button.
 * @property {HTMLButtonElement} aside - Size + price block; opens the sheet in inline mode.
 * @property {HTMLElement} size - Selected size (hidden when none).
 * @property {HTMLElement} price - Price, mirrored from the product price block.
 * @property {HTMLDialogElement} sheet - The size sheet / overlay.
 * @property {HTMLElement} sizes - Container the size rows are rendered into.
 *
 * @extends Component<StickyAddToCartRefs>
 */
/** Ignore further presses on the bar's add button for this long after one (ms). */
const ADD_LOCKOUT_MS = 1000;

/** How long after a bar press the real button's "added" state still counts as the bar's add (ms). */
const ADD_TRACK_WINDOW_MS = 10000;

class StickyAddToCart extends Component {
  requiredRefs = ['bar', 'main', 'label', 'aside', 'size', 'price', 'compare', 'amount', 'sheet', 'sizes'];

  #abortController = new AbortController();

  /** @type {(IntersectionObserver | MutationObserver | ResizeObserver)[]} */
  #observers = [];

  #state = { atcVisible: true, atcAbove: false, pastEnd: false, overlay: false };
  #visible = false;

  /** A purchasable variant is selected (the theme's own add-to-cart validation criterion). */
  #ready = false;
  #klaviyoOpen = false;
  #overlayTimer = 0;
  #closeTimer = 0;
  #focusTimer = 0;
  #keyboard = false;
  #waitlist = false;


  /** @type {string | null | undefined} theme-color content before we touched it */
  #themeColorOriginal;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const section = this.closest('.shopify-section');

    this.mainButton = section?.querySelector('add-to-cart-component button[name="add"]');

    // Waiting-list products: no add to cart, a waiting-list trigger instead
    // (in-house vb-waitlist-button, or the older Klaviyo popup button). Proxy that.
    if (!this.mainButton) {
      const trigger = section?.querySelector('button[data-vb-waitlist-trigger], button[data-klaviyo-form-id-desktop], button[data-klaviyo-form-id-mobile]');
      if (trigger instanceof HTMLButtonElement) {
        this.mainButton = trigger;
        this.#waitlist = true;
        this.dataset.waitlist = 'true';
      }
    }
    this.productForm = section?.querySelector('product-form-component');
    this.sizePicker = section?.querySelector('[class^="ai-size-picker-"]') ?? section?.querySelector('variant-picker');
    // The whole price component, so compare-at / sale changes are mirrored too.
    this.mainPrice = section?.querySelector('.product-details product-price') ?? section?.querySelector('product-price');

    // Nothing to proxy (e.g. product unavailable): stay hidden.
    if (!section || !this.mainButton) return;

    this.#applyModeOverride();
    this.#enterTopLayer();
    this.#watchMainButton();
    this.#watchMainPrice();
    this.#watchScrollPosition();
    this.#watchOwnHeight();
    this.#watchChat();
    this.#watchChatScrollLock();

    document.addEventListener('size-variant-changed', this.#syncSize, { signal });
    document.addEventListener('cart:update', this.#trackAdd, { signal });
    // Theme variant pickers / section re-renders (products without the custom size picker).
    document.addEventListener('variant:update', this.#handleVariantUpdate, { signal });
    document.addEventListener('variant:selected', this.#handleVariantUpdate, { signal });
    section.addEventListener('change', this.#syncSize, { signal });
    this.#watchOverlays();

    const { sheet } = this.refs;
    sheet.addEventListener('cancel', (event) => { event.preventDefault(); this.closeSheet(); }, { signal });
    sheet.addEventListener('click', (event) => { if (event.target === sheet) this.closeSheet(); }, { signal });
    // The desktop popover is anchored to the block's on-screen position.
    window.addEventListener('resize', () => { if (sheet.open) this.closeSheet(); }, { signal });

    this.#syncSize();
    this.#syncPrice();

    // Keep "Add to cart" in full when a long price needs the room (see #fitBar).
    const fitObserver = new ResizeObserver(this.#fitBar);
    fitObserver.observe(this.refs.bar);
    this.#observers.push(fitObserver);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.#observers.forEach((observer) => observer.disconnect());
    this.#observers = [];
    clearTimeout(this.#closeTimer);
    clearTimeout(this.#focusTimer);
    clearInterval(this.#overlayTimer);
    clearInterval(this.#chatTimer);
    if (this.refs.sheet?.open) this.refs.sheet.close();
    document.documentElement.classList.remove('sticky-atc-visible');
    document.documentElement.classList.remove('sticky-atc-chat');
    this.#syncThemeColor(false);
    try { if (this.matches(':popover-open')) this.hidePopover(); } catch { /* no popover support */ }
  }

  /** @returns {'scroll' | 'inline'} */
  get sizeMode() {
    return this.dataset.sizeMode === 'inline' ? 'inline' : 'scroll';
  }

  /**
   * Primary press. No size: scroll to the picker (scroll mode) or open the
   * size list (inline mode). Size selected: press the real button.
   */
  /** @param {Event} [event] */
  async handlePrimary(event) {
    if (!this.mainButton) return;
    this.#keyboard = viaKeyboard(event);

    if (this.#waitlist) {
      this.mainButton.click();
      return;
    }

    if (!this.#ready) {
      if (this.#radios().length === 0) {
        // No size picker but nothing selected (other option types, or a
        // replaced picker block): bring the form into view first so the
        // theme's own validation message lands in front of the user.
        await this.#scrollToSizePicker();
        this.mainButton.click();
        return;
      }
      if (this.sizeMode === 'inline') {
        this.openSheet();
        return;
      }
      await this.#scrollToSizePicker();
      this.mainButton.click();
      return;
    }

    if (this.mainButton.disabled) return;
    // Repeat-press lockout: the real button adds on every press, so a fast
    // double tap on the bar put two in the cart. One press per second.
    const now = performance.now();
    if (now < this.#lockedUntil) return;
    this.#lockedUntil = now + ADD_LOCKOUT_MS;
    this.#addPressedAt = now;
    this.mainButton.click();
  }

  #lockedUntil = 0;

  /** When the bar last pressed the real add button (performance.now), for engagement tracking. */
  #addPressedAt = -Infinity;

  /**
   * VB Engagement (key pdp_sticky_atc): count an add once the cart confirms it.
   * The real button's "added" animation starts on the press, before the cart
   * answers, so it cannot be used. Only this section's form, without an error,
   * shortly after a bar press.
   * @param {Event & { detail?: { data?: { source?: string, didError?: boolean } } }} event
   */
  #trackAdd = (event) => {
    const data = event.detail?.data;
    if (data?.source !== 'product-form-component' || data.didError) return;
    if (!(event.target instanceof Node) || !this.productForm?.contains(event.target)) return;
    if (performance.now() - this.#addPressedAt > ADD_TRACK_WINDOW_MS) return;
    this.#addPressedAt = -Infinity;
    window.vbEngagement?.track?.('add_to_cart', 'pdp_sticky_atc', location.pathname.match(/\/products\/([^/?#]+)/)?.[1] ?? '');
  };

  /** Size + price block: change size in inline mode, otherwise same as primary. */
  /** @param {Event} [event] */
  handleAside(event) {
    this.#keyboard = viaKeyboard(event);
    if (this.#waitlist) return this.handlePrimary(event);
    if (this.sizeMode === 'inline' && this.#radios().length > 0) {
      this.openSheet();
      return;
    }
    return this.handlePrimary(event);
  }

  openSheet() {
    const { sheet, sizes } = this.refs;
    if (this.#radios().length === 0) return;

    clearTimeout(this.#closeTimer);
    this.#buildSizeList();

    this.#anchorSheet();
    if (!sheet.open) sheet.showModal();

    // Force a style flush so the open transition runs from the closed state
    // (synchronous, unlike requestAnimationFrame, which stalls in hidden tabs).
    void sheet.offsetHeight;
    sheet.classList.add('is-open');

    // Keyboard users: move focus to the current size once the panel has
    // settled (focusing mid-transition makes iOS Safari scroll the moving row
    // into view). Pointer users: leave focus on the panel (tabindex="-1") so
    // no focus ring is drawn.
    if (!this.#keyboard) return;
    const focusTarget =
      sizes.querySelector('[aria-current="true"]') ?? sizes.querySelector('.satc__row:not([aria-disabled="true"])');
    this.#focusTimer = window.setTimeout(() => {
      if (sheet.open && focusTarget instanceof HTMLElement) focusTarget.focus({ preventScroll: true });
    }, reduceMotion() ? 0 : 320);
  }

  /**
   * Desktop: position the panel over the black block (same left edge and
   * width, bottom edge on the block's top). Mobile uses the CSS bottom sheet.
   */
  #anchorSheet() {
    const { sheet, bar } = this.refs;
    if (!window.matchMedia('(min-width: 750px)').matches) {
      ['--satc-anchor-left', '--satc-anchor-width', '--satc-anchor-bottom'].forEach((v) => sheet.style.removeProperty(v));
      return;
    }
    const rect = bar.getBoundingClientRect();
    sheet.style.setProperty('--satc-anchor-left', `${rect.left}px`);
    sheet.style.setProperty('--satc-anchor-width', `${rect.width}px`);
    sheet.style.setProperty('--satc-anchor-bottom', `${window.innerHeight - rect.top}px`);
  }

  closeSheet() {
    const { sheet, main } = this.refs;
    if (!sheet.open) return;

    sheet.classList.remove('is-open');
    clearTimeout(this.#focusTimer);

    const finish = () => {
      if (sheet.open) sheet.close();
      if (this.#keyboard) {
        main.focus({ preventScroll: true });
      } else if (document.activeElement instanceof HTMLElement && this.contains(document.activeElement)) {
        // The dialog hands focus back to the button; drop it so no ring shows.
        document.activeElement.blur();
      }
    };

    clearTimeout(this.#closeTimer);
    if (reduceMotion()) finish();
    else this.#closeTimer = window.setTimeout(finish, 300);
  }

  /**
   * A size row was pressed: select the matching radio in the real picker (its
   * own change handling updates the variant and dispatches size-variant-changed).
   * @param {Event} event
   */
  chooseSize(event) {
    this.#keyboard = viaKeyboard(event);
    const row = event.target instanceof Element ? event.target.closest('[data-size-value]') : null;
    if (!(row instanceof HTMLElement) || row.getAttribute('aria-disabled') === 'true') return;

    const value = row.dataset.sizeValue;
    const radio = this.#radios().find((input) => (input.dataset.sizeValue || input.value) === value);
    if (radio && !radio.checked) radio.click();

    this.#syncSize();
    this.closeSheet();
  }

  /** @returns {HTMLInputElement[]} */
  #radios() {
    return [...(this.sizePicker?.querySelectorAll('input[type="radio"]') ?? [])].filter(
      (input) => input instanceof HTMLInputElement
    );
  }

  /** Rebuild the size rows from the real picker so they can never drift from it. */
  #buildSizeList() {
    const soldOut = this.dataset.soldOut || 'Sold out';

    const rows = this.#radios().map((radio) => {
      const value = radio.dataset.sizeValue || radio.value;
      const available = radio.dataset.available !== 'false' && !radio.disabled;

      const row = document.createElement('button');
      row.type = 'button';
      row.className = 'satc__row';
      row.dataset.sizeValue = value;
      row.setAttribute('role', 'listitem');
      row.setAttribute('on:click', '/chooseSize');
      if (radio.checked) row.setAttribute('aria-current', 'true');
      if (!available) row.setAttribute('aria-disabled', 'true');

      const label = document.createElement('span');
      label.className = 'satc__row-label';
      label.textContent = value;
      row.append(label);

      if (!available) {
        const status = document.createElement('span');
        status.className = 'satc__row-status';
        status.textContent = soldOut;
        row.append(status);
      }

      return row;
    });

    this.refs.sizes.replaceChildren(...rows);
  }

  /**
   * Scrolls the size picker into view and resolves once the scroll has
   * settled (`scrollend`, or a timeout for browsers without it).
   * @returns {Promise<void>}
   */
  #scrollToSizePicker() {
    const target = this.sizePicker ?? this.productForm ?? this.mainButton;
    if (!target) return Promise.resolve();

    // Reserve the sticky header's height even when it is currently slid off
    // screen: scrolling up brings it back, and it would otherwise cover the picker.
    const header = document.querySelector('.header-section');
    const headerRect = header?.getBoundingClientRect();
    const headerOffset = headerRect ? Math.max(0, headerRect.bottom, headerRect.height) : 0;

    const top = Math.max(0, target.getBoundingClientRect().top + window.scrollY - headerOffset - 24);
    const instant = reduceMotion() || Math.abs(top - window.scrollY) < 2;

    const firstOption = this.#radios().find((input) => !input.disabled);
    if (firstOption) firstOption.focus({ preventScroll: true });

    window.scrollTo({ top, behavior: instant ? 'instant' : 'smooth' });
    if (instant) return Promise.resolve();

    return new Promise((resolve) => {
      const done = () => {
        clearTimeout(timer);
        document.removeEventListener('scrollend', done);
        resolve();
      };
      const timer = setTimeout(done, 900);
      document.addEventListener('scrollend', done, { once: true });
    });
  }

  /** Mirror label, disabled state, the "Added" flash and the width of the real button. */
  #watchMainButton() {
    if (!this.mainButton) return;

    // Desktop block width tracks the inline button so the two always match.
    this.#syncInlineWidth();
    // Width changes only: Chrome on iOS fires resize on every frame while its
    // toolbars slide (height only), and the button's width cannot change then.
    let lastWidth = window.innerWidth;
    window.addEventListener('resize', () => {
      if (window.innerWidth === lastWidth) return;
      lastWidth = window.innerWidth;
      this.#syncInlineWidth();
    }, { signal: this.#abortController.signal });
    const sizeObserver = new ResizeObserver(this.#syncInlineWidth);
    sizeObserver.observe(this.mainButton);
    this.#observers.push(sizeObserver);

    const observer = new MutationObserver(this.#syncLabel);
    observer.observe(this.mainButton, {
      attributes: true,
      attributeFilter: ['disabled', 'class'],
      childList: true,
      subtree: true,
      characterData: true,
    });
    this.#observers.push(observer);
  }

  #watchMainPrice() {
    if (!this.mainPrice) return;

    const observer = new MutationObserver(this.#syncPrice);
    observer.observe(this.mainPrice, { childList: true, subtree: true, characterData: true });
    this.#observers.push(observer);
  }

  /**
   * Show once the real button has scrolled above the viewport; hide again as
   * soon as it is back on screen or the user reaches the end-of-page content.
   */
  #watchScrollPosition() {
    if (!this.mainButton) return;

    const buttonObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        this.#state.atcVisible = entry.isIntersecting;
        this.#state.atcAbove = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
        this.#render();
      },
      { threshold: 0 }
    );
    buttonObserver.observe(this.mainButton);
    this.#observers.push(buttonObserver);

    // The observer only fires when the button enters or leaves the viewport.
    // If it starts below the fold (mobile) and the page jumps straight past it
    // (anchor link, restored scroll position, a script), it never "leaves", so
    // re-derive above/below from its position on every scroll as well.
    window.addEventListener('scroll', this.#syncAbove, { passive: true, signal: this.#abortController.signal });

    const selectors = HIDE_AT[this.dataset.hideAt ?? 'related'] ?? HIDE_AT.related;
    const targets = new Set(selectors.flatMap((selector) => [...document.querySelectorAll(selector)]));
    if (targets.size === 0) return;

    const inView = new Set();
    const endObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          entry.isIntersecting ? inView.add(entry.target) : inView.delete(entry.target);
        }
        this.#state.pastEnd = inView.size > 0;
        this.#endTargetsInView = [...inView].map((t) => (t.id ? '#' + t.id : '.' + String(t.className).split(' ')[0]).slice(0, 60));
        this.#render();
      },
      { threshold: 0 }
    );
    targets.forEach((target) => endObserver.observe(target));
    this.#observers.push(endObserver);
  }

  /**
   * Chat disc (mobile): shown in the bar while Gorgias reports chat online,
   * standing in for the launcher the bar's housing would otherwise cover.
   * Gorgias loads late and availability changes with agents' hours, so poll.
   */
  #chatTimer = 0;
  #watchChat() {
    this.#syncChat();
    this.#chatTimer = window.setInterval(this.#syncChat, 5000);
  }

  #syncChat = () => {
    let online = false;
    try {
      const gorgias = /** @type {any} */ (window).GorgiasChat;
      online = !!(gorgias && typeof gorgias.isChatOnline === 'function' && gorgias.isChatOnline());
    } catch {
      // Gorgias not ready; treat as offline.
    }
    // Guard: the disc replaces the launcher, so only show it while the
    // launcher frame is present under its known id. If Gorgias ever renames
    // it we could not hide it, so fall back to the plain bar rather than
    // showing two chat icons.
    if (!document.getElementById('chat-button')) online = false;
    this.refs.chat?.classList.toggle('is-on', online);
    document.documentElement.classList.toggle('sticky-atc-chat', online);
    if (online) this.#cloneLauncherIcon();
    this.#fitBar();
  };

  /**
   * Use the live launcher's own SVG (its iframe is same-origin, written by
   * the Gorgias bundle) so the disc shows exactly the artwork customers see.
   * Falls back to the copy in the markup if the frame is not readable.
   */
  #chatIconCloned = false;
  #cloneLauncherIcon() {
    if (this.#chatIconCloned) return;
    try {
      const frame = /** @type {HTMLIFrameElement | null} */ (document.getElementById('chat-button'));
      const svg = frame?.contentDocument?.querySelector('#gorgias-chat-messenger-button svg');
      const icon = this.refs.chatIcon;
      if (!(svg instanceof SVGElement) || !(icon instanceof HTMLElement)) return;
      const clone = /** @type {SVGElement} */ (svg.cloneNode(true));
      clone.removeAttribute('role');
      clone.setAttribute('aria-hidden', 'true');
      icon.replaceChildren(clone);
      this.#chatIconCloned = true;
    } catch {
      // Cross-origin or not rendered yet; keep the markup copy.
    }
  }

  /**
   * Gorgias locks page scroll while its window is open by fixing <html> at
   * inset 0, which drops the scroll position to 0: the page visibly jumps to
   * the top as the window fades in and back down as it closes. While that
   * lock is on, hold <html> where the page was (top: -scrollY, natural
   * height) so nothing moves underneath; restore the position on unlock.
   */
  #lockY = null;
  #lastScrollY = 0;
  #watchChatScrollLock() {
    const root = document.documentElement;
    const { signal } = this.#abortController;
    this.#lastScrollY = window.scrollY;
    window.addEventListener('scroll', () => { if (this.#lockY === null) this.#lastScrollY = window.scrollY; }, { passive: true, signal });
    const observer = new MutationObserver(() => {
      const fixed = root.style.position === 'fixed';
      if (fixed && this.#lockY === null) {
        this.#lockY = this.#lastScrollY;
        root.style.setProperty('top', `-${this.#lockY}px`, 'important');
        root.style.setProperty('bottom', 'auto', 'important');
        root.style.setProperty('height', 'auto', 'important');
      } else if (!fixed && this.#lockY !== null) {
        const y = this.#lockY;
        this.#lockY = null;
        ['top', 'bottom', 'height'].forEach((prop) => root.style.removeProperty(prop));
        if (Math.abs(window.scrollY - y) > 1) window.scrollTo({ top: y, behavior: 'instant' });
      }
    });
    observer.observe(root, { attributes: true, attributeFilter: ['style'] });
    this.#observers.push(observer);
  }

  /** @param {Event} [event] */
  openChat(event) {
    event?.preventDefault();
    try {
      /** @type {any} */ (window).GorgiasChat?.open();
    } catch {
      // Gorgias not ready; nothing to open.
    }
  }

  /** Expose the bar's height so fixed UI (chat launcher) can sit above it. */
  #watchOwnHeight() {
    const observer = new ResizeObserver(() => {
      document.documentElement.style.setProperty('--sticky-atc-height', `${this.offsetHeight}px`);
    });
    observer.observe(this);
    this.#observers.push(observer);
  }

  /**
   * Show the bar as a manual popover so it lives in the browser's top layer
   * (see the stylesheet note). Kept open for the element's lifetime; the
   * visible/hidden state is still driven by .is-visible. Browsers without the
   * Popover API simply keep the ordinary fixed positioning.
   */
  #enterTopLayer() {
    try {
      if (this.hasAttribute('popover') && !this.matches(':popover-open')) this.showPopover();
    } catch {
      this.removeAttribute('popover');
    }
  }

  /**
   * Testing override: ?atc_mode=scroll|inline applies to this page load only,
   * so a link always shows what it says and the theme setting is the default.
   */
  #applyModeOverride() {
    const param = new URLSearchParams(window.location.search).get('atc_mode');
    if (param === 'scroll' || param === 'inline') this.dataset.sizeMode = param;
    try {
      sessionStorage.removeItem(MODE_STORAGE_KEY); // clear any value left by the earlier test links
    } catch {
      // sessionStorage unavailable; nothing to clear.
    }
  }

  #render() {
    const { atcVisible, atcAbove, pastEnd, overlay } = this.#state;
    const showAlways = this.dataset.showMode === 'always';
    const visible = !atcVisible && !pastEnd && !overlay && (showAlways || atcAbove);

    if (visible === this.#visible) {
      this.#exposeState();
      return;
    }
    this.#visible = visible;
    this.#exposeState();

    if (!visible && this.refs.sheet.open) this.closeSheet();

    this.classList.toggle('is-visible', visible);
    this.toggleAttribute('inert', !visible);
    this.setAttribute('aria-hidden', String(!visible));
    document.documentElement.classList.toggle('sticky-atc-visible', visible);
    this.#syncThemeColor(visible);
    this.#syncChat();
  }

  /**
   * iOS Safari tints its translucent toolbar from <meta name="theme-color">
   * (falling back to sampling the page). While the bar is showing, set it to
   * the bar's background so the toolbar reads solid beneath the block.
   */
  #syncThemeColor(visible) {
    let meta = document.querySelector('meta[name="theme-color"]');

    if (visible) {
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = 'theme-color';
        meta.dataset.satcCreated = 'true';
        document.head.append(meta);
      }
      if (this.#themeColorOriginal === undefined) this.#themeColorOriginal = meta.getAttribute('content');
      meta.setAttribute('content', getComputedStyle(this).backgroundColor || '#ffffff');
      return;
    }

    if (!meta || this.#themeColorOriginal === undefined) return;
    if (meta.dataset.satcCreated) meta.remove();
    else meta.setAttribute('content', this.#themeColorOriginal ?? '');
    this.#themeColorOriginal = undefined;
  }

  #syncLabel = () => {
    if (!this.mainButton) return;

    const { bar, main, label, aside } = this.refs;

    if (this.#waitlist) {
      const text = this.mainButton.textContent?.trim();
      if (text) label.textContent = text;
      main.disabled = this.mainButton.disabled;
      bar.classList.toggle('is-disabled', main.disabled);
      aside.setAttribute('aria-hidden', 'true');
      aside.tabIndex = -1;
      main.dataset.source = 'waitlist';
      aside.dataset.source = 'waitlist';
      return;
    }

    const realText = this.mainButton.querySelector('.add-to-cart-text__content')?.textContent?.trim();

    const hasPicker = this.#radios().length > 0;

    if (this.#ready || !hasPicker) {
      // Mirror the real button (Add to cart / Sold out / whatever the theme says).
      if (realText) label.textContent = realText;
      main.disabled = this.#ready ? this.mainButton.disabled : false;
    } else {
      label.textContent = this.dataset.prompt || 'Select a size';
      main.disabled = false;
    }

    bar.classList.toggle('is-disabled', main.disabled);
    main.classList.toggle('atc-added', this.mainButton.classList.contains('atc-added'));

    // The aside is the "change size" control only in inline mode with a size
    // picker present; otherwise it duplicates the primary action, so keep it
    // out of the tab order.
    const inline = this.sizeMode === 'inline' && hasPicker;
    // Engagement placement label, read by engagement.js when a button is pressed.
    main.dataset.source = this.#ready ? 'add' : 'choose_size';
    aside.dataset.source = inline ? 'choose_size' : main.dataset.source;
    aside.setAttribute('aria-label', inline ? 'Change size' : label.textContent ?? '');
    aside.setAttribute('aria-hidden', String(!inline));
    aside.tabIndex = inline ? 0 : -1;
  };

  #syncSize = () => {
    const checked = this.#radios().find((input) => input.checked);
    const value = checked ? checked.dataset.sizeValue || checked.value : '';

    this.#ready = Boolean(this.#variantId());
    this.refs.size.textContent = value;
    this.refs.size.hidden = !value;

    this.#syncLabel();
    this.#checkBlocked();
    this.#fitBar();
  };

  /** The variant the real form would submit; empty until a size is chosen on sized products. */
  #variantId() {
    const input = this.productForm?.querySelector('input[name="id"]') ?? this.closest('.shopify-section')?.querySelector('form[action*="/cart/add"] input[name="id"]');
    return input instanceof HTMLInputElement ? input.value.trim() : '';
  }

  /** Theme variant pickers re-render parts of the section; re-acquire what may have been replaced. */
  #handleVariantUpdate = () => {
    if (this.mainPrice && !this.mainPrice.isConnected) {
      const section = this.closest('.shopify-section');
      this.mainPrice = section?.querySelector('.product-details product-price') ?? section?.querySelector('product-price') ?? null;
      this.#watchMainPrice();
    }
    this.#syncSize();
    this.#syncPrice();
  };

  /**
   * Being in the top layer, the bar would paint over overlays that are not
   * themselves top-layer (mobile menu <details>, drawers, Gorgias chat,
   * Klaviyo pop-ups, the cookie banner) and would sit under every later
   * <dialog> (cart, search). Hide it while any of those is open.
   */
  #watchOverlays() {
    const { signal } = this.#abortController;
    const check = () => this.#checkBlocked();
    window.addEventListener('klaviyoForms', (event) => {
      const type = /** @type {any} */ (event).detail?.type;
      if (type === 'open') this.#klaviyoOpen = true;
      if (type === 'close' || type === 'submit') this.#klaviyoOpen = false;
      check();
    }, { signal });
    document.addEventListener('toggle', check, { capture: true, signal });
    document.addEventListener('close', check, { capture: true, signal });
    this.#overlayTimer = window.setInterval(() => {
      if (document.visibilityState === 'visible') check();
    }, 400);
    check();
  }

  /** Anything that should take the bar off screen: overlays, a missing/hidden real button, or a sold-out selection. */
  #checkBlocked = () => {
    this.#syncAbove();
    const reasons = [];
    if (this.#overlayOpen()) reasons.push('overlay');
    if (this.#buttonHidden()) reasons.push('button-hidden');
    if (this.#soldOutSelected()) reasons.push('sold-out');
    this.#blockReasons = reasons;
    const blocked = reasons.length > 0;
    if (blocked !== this.#state.overlay) {
      this.#state.overlay = blocked;
      this.#render();
    } else {
      this.#exposeState();
    }
  };

  /** @type {string[]} */
  #blockReasons = [];
  /** @type {string[]} */
  #endTargetsInView = [];

  /** Debug/QA: mirror the decision inputs onto a data attribute (read-only, no behaviour). */
  #exposeState() {
    const { atcVisible, atcAbove, pastEnd, overlay } = this.#state;
    this.dataset.satcState = JSON.stringify({ atcVisible, atcAbove, pastEnd, blocked: overlay, reasons: this.#blockReasons, end: this.#endTargetsInView, visible: this.#visible });
  }

  /**
   * The selected size is sold out: the page shows the theme's own "Sold out"
   * button (and, on waiting-list products, the Klaviyo trigger), so the bar
   * steps aside rather than duplicating a dead control.
   */
  #soldOutSelected() {
    if (this.#waitlist) return false;
    const checked = this.#radios().find((input) => input.checked);
    if (checked && checked.dataset.available === 'false') return true;
    if (!this.#ready || !this.mainButton) return false;
    const text = this.mainButton.querySelector('.add-to-cart-text__content')?.textContent ?? '';
    return /sold\s*out/i.test(text);
  }

  #overlayOpen() {
    const isVisible = (element) =>
      element instanceof HTMLElement && element.getClientRects().length > 0 && getComputedStyle(element).visibility !== 'hidden';

    const foreignDialog = [...document.querySelectorAll('dialog[open]')].some((dialog) => !this.contains(dialog));
    if (foreignDialog) return true;
    if (document.querySelector('#Details-menu-drawer-container[open], .drawer[data-open="true"]')) return true;
    if (this.#klaviyoOpen) return true;
    if (isVisible(document.querySelector('#shopify-pc__banner'))) return true;
    try {
      const gorgias = /** @type {any} */ (window).GorgiasChat;
      if (gorgias && typeof gorgias.isOpen === 'function' && gorgias.isOpen()) return true;
    } catch {
      // Gorgias not ready; ignore.
    }
    return false;
  }

  #aboveQueued = false;
  #syncAbove = () => {
    if (this.#aboveQueued) return;
    this.#aboveQueued = true;
    const run = () => {
      this.#aboveQueued = false;
      if (!this.mainButton || this.#state.atcVisible) return;
      const above = this.mainButton.getBoundingClientRect().bottom <= 0;
      if (above !== this.#state.atcAbove) {
        this.#state.atcAbove = above;
        this.#render();
      }
    };
    // rAF keeps it to one check per frame; the timeout covers tabs where rAF is paused.
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(run);
    else setTimeout(run, 50);
  };

  #syncInlineWidth = () => {
    const width = this.mainButton?.getBoundingClientRect().width;
    if (width) this.style.setProperty('--satc-inline-atc-width', `${Math.round(width)}px`);
  };

  #syncPrice = () => {
    const { amount, compare } = this.refs;
    // The bar leaves out the trailing currency code ("$30,245 MXN" -> "$30,245") so it fits beside the
    // chat disc on mobile; an AED/SAR prefix stays, as it stands in for the symbol.
    const text = (el) => {
      if (!el) return undefined;
      const copy = el.cloneNode(true);
      copy.querySelectorAll('.price__code:not(.price__code--prefix)').forEach((code) => code.remove());
      return copy.textContent.trim();
    };
    const price = text(this.mainPrice?.querySelector('.price'));
    const compareAt = text(this.mainPrice?.querySelector('.compare-at-price')) ?? '';

    if (price) amount.textContent = price;
    compare.textContent = compareAt;
    compare.hidden = !compareAt || compareAt === price;
    this.#fitBar();
  };

  /**
   * Long prices (₩2,550,000, $1,167,445) beside the chat disc on small phones leave too little room for the
   * label. Step 1 tightens the letter spacing; step 2 also drops the size letter (it stays selected in the
   * picker). The price is never shortened; the CSS ellipsis on the label is only a last resort.
   */
  #fitBar = () => {
    const { label } = this.refs;
    if (!label || label.clientWidth === 0) return;
    const tight = () => label.scrollWidth > label.clientWidth + 1;
    this.classList.remove('satc--tight', 'satc--tighter');
    if (!tight()) return;
    this.classList.add('satc--tight');
    if (!tight()) return;
    this.classList.add('satc--tighter');
  };

  /** The bar must never outlive the control it proxies: removed, display:none, or otherwise not rendered. */
  #buttonHidden() {
    const button = this.mainButton;
    if (!button || !button.isConnected) return true;
    if (button.getClientRects().length === 0) return true;
    return getComputedStyle(button).visibility === 'hidden';
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
